/**
 * RouteRatingsChart Component
 *
 * Displays two lists of routes: highest rated and lowest rated.
 * Helps gym owners identify quality routes and routes needing attention.
 */

import { type CSSProperties } from 'react';
import type { VGrade } from '@flash/shared';

/**
 * Route with stats for analytics display
 */
interface RouteWithStats {
  id: string;
  name: string;
  officialGrade: VGrade;
  totalSends: number;
  totalAttempts: number;
  averageRating: number | null;
  ratingCount: number;
  setterName: string;
}

/**
 * Props for RouteRatingsChart component
 */
interface RouteRatingsChartProps {
  /** Routes sorted by rating descending */
  highestRated: RouteWithStats[];
  /** Routes sorted by rating ascending (min 3 ratings) */
  lowestRated: RouteWithStats[];
}

/**
 * Render star display for a rating
 */
const StarDisplay = ({ rating }: { rating: number }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <span style={starContainerStyle}>
      {'*'
        .repeat(fullStars)
        .split('')
        .map((_, i) => (
          <span key={`full-${i}`} style={fullStarStyle}>
            &#9733;
          </span>
        ))}
      {hasHalf && <span style={halfStarStyle}>&#9733;</span>}
      {'*'
        .repeat(emptyStars)
        .split('')
        .map((_, i) => (
          <span key={`empty-${i}`} style={emptyStarStyle}>
            &#9733;
          </span>
        ))}
      <span style={ratingNumberStyle}>({rating.toFixed(1)})</span>
    </span>
  );
};

/**
 * Route list item component
 */
const RouteItem = ({ route }: { route: RouteWithStats }) => (
  <div style={routeItemStyle}>
    <div style={routeInfoStyle}>
      <span style={routeNameStyle}>{route.name}</span>
      <div style={routeMetaStyle}>
        <span style={gradeBadgeStyle}>{route.officialGrade}</span>
        <span style={ratingCountStyle}>{route.ratingCount} ratings</span>
      </div>
    </div>
    <div style={ratingDisplayStyle}>
      {route.averageRating !== null && <StarDisplay rating={route.averageRating} />}
    </div>
  </div>
);

/**
 * RouteRatingsChart displays highest and lowest rated routes
 *
 * @example
 * ```tsx
 * <RouteRatingsChart
 *   highestRated={analytics.highestRatedRoutes}
 *   lowestRated={analytics.lowestRatedRoutes}
 * />
 * ```
 */
export const RouteRatingsChart = ({ highestRated, lowestRated }: RouteRatingsChartProps) => {
  const hasAnyData = highestRated.length > 0 || lowestRated.length > 0;

  if (!hasAnyData) {
    return (
      <div style={containerStyle}>
        <h3 style={titleStyle}>Route Ratings</h3>
        <div style={emptyStateStyle}>
          <p style={emptyTextStyle}>No rated routes yet.</p>
          <p style={emptySubtextStyle}>Routes will appear here once they receive ratings.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h3 style={titleStyle}>Route Ratings</h3>
      <p style={subtitleStyle}>Compare your best and lowest performing routes</p>

      <div style={columnsStyle}>
        {/* Highest Rated */}
        <div style={columnStyle}>
          <div style={columnHeaderStyle}>
            <span style={columnIconStyle}>&#9733;</span>
            <h4 style={columnTitleStyle}>Highest Rated</h4>
          </div>
          {highestRated.length > 0 ? (
            <div style={routeListStyle}>
              {highestRated.slice(0, 5).map((route) => (
                <RouteItem key={route.id} route={route} />
              ))}
            </div>
          ) : (
            <p style={noDataStyle}>No rated routes yet</p>
          )}
        </div>

        {/* Lowest Rated */}
        <div style={columnStyle}>
          <div style={{ ...columnHeaderStyle, backgroundColor: '#fef2f2' }}>
            <span style={{ ...columnIconStyle, color: '#dc2626' }}>!</span>
            <h4 style={columnTitleStyle}>Needs Attention</h4>
          </div>
          {lowestRated.length > 0 ? (
            <div style={routeListStyle}>
              {lowestRated.slice(0, 5).map((route) => (
                <RouteItem key={route.id} route={route} />
              ))}
            </div>
          ) : (
            <p style={noDataStyle}>No routes with enough ratings</p>
          )}
          {lowestRated.length > 0 && (
            <p style={noticeStyle}>Routes with 3+ ratings and lower scores</p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#1f2937',
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: '#6b7280',
};

const emptyStateStyle: CSSProperties = {
  padding: '32px',
  textAlign: 'center',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  border: '1px dashed #d1d5db',
};

const emptyTextStyle: CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '0.875rem',
  color: '#374151',
  fontWeight: 500,
};

const emptySubtextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.8125rem',
  color: '#6b7280',
};

const columnsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '20px',
};

const columnStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  overflow: 'hidden',
};

const columnHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  backgroundColor: '#f0fdf4',
  borderBottom: '1px solid #e5e7eb',
};

const columnIconStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#16a34a',
};

const columnTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#1f2937',
};

const routeListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const routeItemStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  borderBottom: '1px solid #f3f4f6',
  gap: '12px',
};

const routeInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
  flex: 1,
};

const routeNameStyle: CSSProperties = {
  color: '#1f2937',
  fontWeight: 500,
  fontSize: '0.875rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const routeMetaStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const gradeBadgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  backgroundColor: '#e5e7eb',
  borderRadius: '10px',
  fontWeight: 600,
  color: '#374151',
  fontSize: '0.6875rem',
};

const ratingCountStyle: CSSProperties = {
  fontSize: '0.6875rem',
  color: '#9ca3af',
};

const ratingDisplayStyle: CSSProperties = {
  flexShrink: 0,
};

const starContainerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1px',
};

const fullStarStyle: CSSProperties = {
  color: '#fbbf24',
  fontSize: '0.875rem',
};

const halfStarStyle: CSSProperties = {
  color: '#fcd34d',
  fontSize: '0.875rem',
};

const emptyStarStyle: CSSProperties = {
  color: '#e5e7eb',
  fontSize: '0.875rem',
};

const ratingNumberStyle: CSSProperties = {
  marginLeft: '4px',
  fontSize: '0.75rem',
  color: '#6b7280',
  fontWeight: 500,
};

const noDataStyle: CSSProperties = {
  padding: '20px 16px',
  margin: 0,
  fontSize: '0.875rem',
  color: '#9ca3af',
  textAlign: 'center',
};

const noticeStyle: CSSProperties = {
  padding: '8px 16px',
  margin: 0,
  fontSize: '0.6875rem',
  color: '#6b7280',
  backgroundColor: '#f9fafb',
  borderTop: '1px solid #e5e7eb',
};

export default RouteRatingsChart;
